<template>
    <aside class="forum-detail">
        <div v-if="!forum" class="forum-empty forum-detail__empty">
            <span class="forum-empty__icon" aria-hidden="true">
                <fa icon="fa-solid fa-comments" />
            </span>
            <h3>{{ $t('student.forum.selectForum') }}</h3>
        </div>

        <template v-else>
            <div class="forum-detail__back">
                <button type="button" class="forum-back" @click="$emit('back')">
                    <fa icon="fa-solid fa-chevron-left" />
                    {{ $t('common.back') }}
                </button>
            </div>

            <section class="forum-detail__card">
                <h2 class="forum-detail__title">{{ forum.title }}</h2>
                <p v-if="forumDescription" class="forum-detail__description">
                    {{ forumDescription }}
                </p>
                <div class="forum-detail__meta">
                    <span class="forum-detail__author">
                        <ForumAvatar :name="forum.userName" size="sm" />
                        {{ forum.userName }}
                    </span>
                    <span class="forum-detail__meta-item">
                        <fa icon="fa-solid fa-calendar" aria-hidden="true" />
                        {{ formatDate(forum.dateInsert) }}
                    </span>
                    <span class="forum-detail__meta-item">
                        <fa icon="fa-solid fa-comment" aria-hidden="true" />
                        {{ commentCount }}
                        {{ commentCount === 1 ? $t('student.forum.commentOne') : $t('student.forum.commentMany') }}
                    </span>
                </div>
            </section>

            <div v-if="loading" class="forum-loading forum-detail__loading">
                <v-progress-circular indeterminate color="#1ac7d0" size="36" />
            </div>

            <template v-else>
                <div class="forum-detail__scroll">
                    <ul v-if="comments.length" class="forum-comments">
                        <li v-for="comment in comments" :key="comment.id">
                            <ForumCommentCard :comment="comment" />
                        </li>
                    </ul>
                    <p v-else class="forum-comments-empty">
                        {{ $t('student.forum.noCommentsYet') }}
                    </p>
                </div>

                <form class="forum-editor" @submit.prevent="sendComment">
                    <label class="forum-editor__title" for="forum_view_comment_text">
                        {{ $t('student.forum.addComment') }}
                    </label>
                    <textarea
                        id="forum_view_comment_text"
                        v-model="commentText"
                        class="forum-editor__input"
                        rows="3"
                        :placeholder="$t('student.forum.writeComment')"
                        :aria-label="$t('student.forum.commentLabel')"
                    ></textarea>
                    <div class="forum-editor__actions">
                        <button
                            id="forum_view_send_comment"
                            type="submit"
                            class="forum-send"
                            :disabled="!commentText || loadingSend"
                        >
                            <span v-if="loadingSend" class="forum-send__spinner" aria-hidden="true"></span>
                            <fa v-else icon="fa-solid fa-paper-plane" />
                            <span>{{ loadingSend ? $t('student.forum.sending') : $t('common.send') }}</span>
                        </button>
                    </div>
                </form>
            </template>
        </template>
    </aside>
</template>

<script>
    import { mapState } from 'vuex';
    import api from '@/utils/portal/forum.js';
    import ForumAvatar from './ForumAvatar.vue';
    import ForumCommentCard from './ForumCommentCard.vue';

    export default {
        name: 'ForumDetailPanel',
        components: { ForumAvatar, ForumCommentCard },
        props: {
            forum: {
                type: Object,
                default: null,
            },
        },
        emits: ['back', 'count'],
        data() {
            return {
                comments: [],
                commentText: '',
                loading: false,
                loadingSend: false,
            };
        },
        computed: {
            ...mapState(['token']),
            forumDescription() {
                return String(this.forum?.description || '').trim();
            },
            commentCount() {
                return Array.isArray(this.comments) ? this.comments.length : 0;
            },
        },
        watch: {
            forum: {
                immediate: true,
                handler(forum) {
                    this.commentText = '';
                    this.comments = [];
                    if (forum && forum.id != null && forum.id !== '') {
                        this.fetchComments();
                    }
                },
            },
        },
        methods: {
            formatDate(date) {
                if (!date) return '-';
                const parsed = new Date(date);
                if (Number.isNaN(parsed.getTime())) return '-';
                return parsed.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            },
            async fetchComments() {
                if (!this.forum) return;
                this.loading = true;
                try {
                    const response = await api.getCommentsByForumId(this.token, this.forum.id);
                    this.comments = Array.isArray(response) ? response : [];
                    this.$emit('count', this.comments.length);
                } catch (error) {
                    console.error('Erro ao carregar comentários:', error);
                } finally {
                    this.loading = false;
                }
            },
            async sendComment() {
                if (!this.commentText || !this.forum) return;
                this.loadingSend = true;
                try {
                    await api.newComment(this.token, {
                        comment: this.commentText,
                        idForum: this.forum.id,
                    });
                    this.commentText = '';
                    await this.fetchComments();
                } catch (error) {
                    console.error('Erro ao enviar comentário:', error);
                } finally {
                    this.loadingSend = false;
                }
            },
        },
    };
</script>
